import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { Button } from '../../../../components-v2/Button';
import { numberToFormattedCount } from '../../../../../models/UiUtils';
import { AmountFilterOption } from '../models';
import { IDonationDayOption, getDonationDayOptions } from '../utils';
import { styleSheet } from './styles';

interface DonationSearchDescriptionProps {
	totalResults?: number;
	viewMode?: 'donors' | 'donations';
	searchFieldValue?: string;
	selectedCampaign?: Api.IDonationCampaign;
	selectedTag?: Api.IAccountTag;
	selectedAmountFilter?: AmountFilterOption;
	minimumDonation?: string;
	maximumDonation?: string;
	hasDonatedMinDate?: string;
	hasDonatedMaxDate?: string;
	isFirstDonation?: boolean;
	notDonatedMinDate?: string;
	notDonatedMaxDate?: string;
	onClearSearch: () => void;
}

const findMatchingOption = (minDate: string, maxDate: string): IDonationDayOption | null => {
	if (!minDate) {
		return null;
	}
	const donationDayOptions = getDonationDayOptions();
	for (const opt of donationDayOptions) {
		if (opt.maxValue && opt.value === minDate && opt.maxValue === maxDate) {
			return opt;
		}
		if (!opt.maxValue && opt.key !== 'Custom' && opt.value === minDate && !maxDate) {
			return opt;
		}
	}
	return null;
};

const formatDateRangeDescription = (minDate: string, maxDate: string): string => {
	const matched = findMatchingOption(minDate, maxDate);
	if (matched) {
		if (matched.key === 'ThisYear') {
			return 'this year';
		}
		if (matched.key === 'LastYear') {
			return 'last year';
		}
		return `in the last ${matched.label}`;
	}
	if (minDate && maxDate) {
		return `between ${moment(minDate).format('MMM D, YYYY')} and ${moment(maxDate).format('MMM D, YYYY')}`;
	}
	if (minDate) {
		return `since ${moment(minDate).format('MMM D, YYYY')}`;
	}
	return '';
};

export const DonationSearchDescription: React.FC<DonationSearchDescriptionProps> = ({
	totalResults,
	viewMode,
	searchFieldValue,
	selectedCampaign,
	selectedTag,
	selectedAmountFilter,
	minimumDonation,
	maximumDonation,
	hasDonatedMinDate,
	hasDonatedMaxDate,
	isFirstDonation,
	notDonatedMinDate,
	notDonatedMaxDate,
	onClearSearch,
}) => {
	const parts = React.useMemo(() => {
		const result: string[] = [];

		// Name search
		if (searchFieldValue) {
			result.push(`for "${searchFieldValue}"`);
		}

		// Campaign
		if (selectedCampaign?.name) {
			result.push(`in campaign "${selectedCampaign.name}"`);
		}

		// Tag
		if (selectedTag?.name) {
			result.push(`tagged "${selectedTag.name}"`);
		}

		// Amount
		if (minimumDonation || maximumDonation) {
			const duration = selectedAmountFilter === AmountFilterOption.Lifetime ? 'lifetime' : '1-time';
			if (minimumDonation && maximumDonation) {
				result.push(`with ${duration} donations $${minimumDonation}-$${maximumDonation}`);
			} else if (minimumDonation) {
				result.push(`with ${duration} donations $${minimumDonation}+`);
			} else if (maximumDonation) {
				result.push(`with ${duration} donations up to $${maximumDonation}`);
			}
		}

		// Has Donated
		if (hasDonatedMinDate) {
			const dateDesc = formatDateRangeDescription(hasDonatedMinDate, hasDonatedMaxDate);
			if (isFirstDonation) {
				result.push(`whose first donation was ${dateDesc}`);
			} else {
				result.push(`who donated ${dateDesc}`);
			}
		}

		// Has NOT Donated
		if (notDonatedMinDate) {
			const dateDesc = formatDateRangeDescription(notDonatedMinDate, notDonatedMaxDate);
			result.push(`who have not donated ${dateDesc}`);
		}

		return result;
	}, [
		searchFieldValue,
		selectedCampaign,
		selectedTag,
		selectedAmountFilter,
		minimumDonation,
		maximumDonation,
		hasDonatedMinDate,
		hasDonatedMaxDate,
		isFirstDonation,
		notDonatedMinDate,
		notDonatedMaxDate,
	]);

	const hasCountPrefix = totalResults != null && viewMode != null;

	if (!hasCountPrefix && parts.length === 0) {
		return null;
	}

	return (
		<div className={css(styleSheet.container)}>
			<span className={css(styleSheet.description)}>
				{hasCountPrefix ? (
					<>
						<span className={css(styleSheet.bold)}>{numberToFormattedCount(totalResults)}</span>
						{` ${viewMode === 'donors' ? 'donors' : 'donations'}`}
						{parts.length > 0 ? ` ${parts.join(' ')}` : ''}
					</>
				) : (
					<>Filtering: {parts.join(' ')}</>
				)}
			</span>
			{parts.length > 0 && (
				<Button variant='link' size='sm' onClick={onClearSearch}>
					Clear Search
				</Button>
			)}
		</div>
	);
};
